// Modal (DESIGN §5.11): confirmações no painel lg e no aluno quando não é folha.
// No celular do painel, use `ModalOuSheet` (vira folha abaixo de 1024 px).
import { useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import { useDesktop, useFocoPreso, useTravarRolagem } from "../lib/hooks";
import { Botao } from "./Botao";
import { Sheet } from "./Sheet";

export interface PropsModal {
  aberto: boolean;
  aoFechar: () => void;
  titulo: ReactNode;
  descricao?: ReactNode;
  /** Ações à direita (primária por último). */
  acoes?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** Na folha (celular), botão "Fechar" no topo (ex.: Recusar pedido, prancha pedido-transferencia "recusar-cel"). */
  fecharNaFolha?: boolean;
}

export function Modal({ aberto, aoFechar, titulo, descricao, acoes, children, className }: PropsModal) {
  const ref = useRef<HTMLDivElement>(null);
  const idTitulo = useId();
  const idDescricao = useId();
  useFocoPreso(ref, aberto, aoFechar);
  useTravarRolagem(aberto);
  if (!aberto) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div aria-hidden className="absolute inset-0 bg-veu animate-fade-entra" onClick={aoFechar} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        aria-describedby={descricao ? idDescricao : undefined}
        tabIndex={-1}
        className={cn(
          "relative w-full max-w-md rounded-folha bg-painel p-6 shadow-modal animate-pop focus:outline-none",
          className,
        )}
      >
        <h2 id={idTitulo} className="text-h2 text-tinta">
          {titulo}
        </h2>
        {descricao && (
          <p id={idDescricao} className="mt-2 text-apoio text-texto-2">
            {descricao}
          </p>
        )}
        {children && <div className="mt-4">{children}</div>}
        {acoes && <div className="mt-6 flex flex-wrap justify-end gap-3">{acoes}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Modal no desktop (≥ 1024), folha no celular — padrão do painel. */
export function ModalOuSheet(props: PropsModal) {
  const desktop = useDesktop();
  if (desktop) return <Modal {...props} />;
  return (
    <Sheet
      aberto={props.aberto}
      aoFechar={props.aoFechar}
      titulo={props.titulo}
      acoes={props.acoes}
      comFechar={props.fecharNaFolha}
    >
      {props.descricao && <p className="text-apoio text-texto-2">{props.descricao}</p>}
      {/* 16 entre a descrição e o conteúdo, como no Modal (mt-4) */}
      {props.children && (props.descricao ? <div className="mt-4">{props.children}</div> : props.children)}
    </Sheet>
  );
}

export interface PropsConfirmar {
  aberto: boolean;
  aoFechar: () => void;
  aoConfirmar: () => void;
  titulo: ReactNode;
  /** Diga a consequência ("Os 10 pontos saem do extrato do aluno."). */
  descricao?: ReactNode;
  /** Verbo explícito ("Remover check-in"). */
  textoConfirmar: string;
  textoCancelar?: string;
  perigo?: boolean;
  carregando?: boolean;
}

/** Confirmação destrutiva/importante com verbo específico (DESIGN §6). */
export function Confirmar({
  aberto,
  aoFechar,
  aoConfirmar,
  titulo,
  descricao,
  textoConfirmar,
  textoCancelar = "Cancelar",
  perigo,
  carregando,
}: PropsConfirmar) {
  return (
    <ModalOuSheet
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={titulo}
      descricao={descricao}
      acoes={
        <>
          <Botao variante="secundario" onClick={aoFechar} className="max-lg:order-2 max-lg:w-full">
            {textoCancelar}
          </Botao>
          <Botao
            variante={perigo ? "perigo" : "tinta"}
            onClick={aoConfirmar}
            carregando={carregando}
            className="max-lg:order-1 max-lg:w-full"
            data-autofoco
          >
            {textoConfirmar}
          </Botao>
        </>
      }
    />
  );
}
